import Link from "next/link";
import { cn } from "utils/utils";
import { SignUpButtonCTA } from "modules/auth/components/SignUpButtonCTA";
import { ReactNode } from "react";

interface EmptyStateCardProps {
  icon?: ReactNode;
  title: string;
  description: string;
  ctaText?: string;
  ctaLink?: string;
  /** Action-based CTA — renders a button instead of a Link when provided (without ctaLink) */
  onCtaClick?: () => void;
  ctaLoading?: boolean;
  variant?: "signed-out" | "signed-in" | "compact";
  showWaitlistCTA?: boolean;
  className?: string;
}

export function EmptyStateCard({
  icon,
  title,
  description,
  ctaText,
  ctaLink,
  onCtaClick,
  ctaLoading,
  variant = "signed-in",
  showWaitlistCTA = false,
  className,
}: EmptyStateCardProps) {
  // Signed-out variant: Vertical centered layout with waitlist CTA
  if (variant === "signed-out" || showWaitlistCTA) {
    return (
      <div
        className={cn(
          "bg-base-200/20 flex flex-col items-center justify-center rounded-lg p-8 text-center",
          className
        )}
      >
        {icon && (
          <div className="bg-primary/20 mb-4 flex h-16 w-16 items-center justify-center rounded-full">
            {icon}
          </div>
        )}
        <h4 className="text-base-content mb-2 text-lg font-semibold">
          {title}
        </h4>
        <p className="text-base-content/70 mb-4 max-w-md text-sm">
          {description}
        </p>
        <SignUpButtonCTA size="sm" variant="navbar" />
      </div>
    );
  }

  // Compact variant: Vertical centered minimal empty state
  if (variant === "compact") {
    return (
      <div
        className={cn(
          "bg-base-200/20 flex h-full w-full items-center justify-center rounded-lg p-6",
          className
        )}
      >
        <div className="flex flex-col items-center text-center">
          {icon && (
            <div className="bg-primary/20 mb-3 flex h-12 w-12 items-center justify-center rounded-full">
              {icon}
            </div>
          )}
          <h4 className="text-base-content mb-2 text-base font-semibold">
            {title}
          </h4>
          <p className="text-base-content/70 mb-3 max-w-sm text-sm">
            {description}
          </p>
          {ctaText && (ctaLink ? (
            <Link href={ctaLink} className="btn btn-sm btn-primary">
              {ctaText}
            </Link>
          ) : onCtaClick ? (
            <button
              type="button"
              onClick={onCtaClick}
              disabled={ctaLoading}
              className="btn btn-sm btn-primary"
            >
              {ctaLoading && <span className="loading loading-spinner loading-xs" />}
              {ctaText}
            </button>
          ) : null)}
        </div>
      </div>
    );
  }

  // Signed-in variant: Full vertical centered empty state with icon and CTA
  return (
    <div
      className={cn(
        "bg-base-200/20 flex flex-col items-center justify-center rounded-lg p-8 text-center",
        className
      )}
    >
      {icon && (
        <div className="bg-primary/20 mb-4 flex h-16 w-16 items-center justify-center rounded-full">
          {icon}
        </div>
      )}
      <h4 className="text-base-content mb-2 text-lg font-semibold">{title}</h4>
      <p className="text-base-content/70 mb-4 max-w-md text-sm">
        {description}
      </p>
      {ctaText && (ctaLink ? (
        <Link href={ctaLink} className="btn btn-sm btn-primary">
          {ctaText}
        </Link>
      ) : onCtaClick ? (
        <button
          type="button"
          onClick={onCtaClick}
          disabled={ctaLoading}
          className="btn btn-sm btn-primary"
        >
          {ctaLoading && <span className="loading loading-spinner loading-xs" />}
          {ctaText}
        </button>
      ) : null)}
    </div>
  );
}
